import {
  Box,
  color,
  Flex,
  FullScreenLoading,
  H1,
  H4,
  Text,
} from '@bfa/common-ui';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import styled from 'styled-components';

import AcquisitionPageHead from '../../../../components/AcquisitionPageHead';
import Error from '../../../../components/Error';
import ErrorBanner from '../../../../components/ErrorBanner';
import Layout from '../../../../components/Layout';
import Disclaimers from '../../components/Disclaimers';
import { BoldedButton } from '../../components/GbxUpgradeButton/GbxUpgradeButtonStyles';
import LearnMoreModal from '../../components/LearnMoreModal';
import PaymentMethod from '../../components/PaymentMethod';
import { NO_PAYMENT_METHOD_ERROR_MESSAGE } from '../../constants';
import useOrganicUpgradeToGbx from '../../hooks/useOrganicUpgradeToGbx';
import useUpgradeToGBXCheckout from '../../hooks/useUpgradeGbxCheckout';

const BorderBox = styled(Box)`
  border: 0.1rem solid ${color('grayLight')};
`;

const BeigeBox = styled(Box)`
  background-color: ${color('beige')};
  ${Text}:not(:first-child) {
    margin-top: 1.5rem;
  }
`;

const TextIconStyled = styled(Text)`
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${color('grayMedium')};
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
const GbxUpgradeCheckout = (): JSX.Element => {
  const {
    checkoutPage,
    isNewMembershipPageInUse,
    onCrossClick,
    onLogoClick,
    logAnalytics,
    toggleModal,
    showModal,
    loading: queryLoading,
    queryError,
    experimentId,
    isWithGift,
    paymentRetrialEnabled,
    gbxMetaTitle,
    gbxMetaDescription,
  } = useUpgradeToGBXCheckout();

  const {
    handleUpgradeToGbx,
    loading: upgradeLoading,
    error: upgradeError,
  } = useOrganicUpgradeToGbx(
    isNewMembershipPageInUse,
    logAnalytics,
    paymentRetrialEnabled ?? false,
    experimentId,
    isWithGift
  );

  const {
    checkoutheader,
    checkoutpaymentsubheader,
    checkoutupgradeprogram,
    checkoutprogramupgradeprice,
    checkoutnewannualupgradeprice,
    paymentIcon,
    paymentInfo,
    checkoutcancellationtext,
    checkoutnotificationmonthlybulletpoint,
    checkoutsubscriptiondisclaimer,
    checkoutshippingdisclaimer,
    checkoutshippinglearnmore,
    checkoutbuttoncta,
    checkoutnopaymenterrormsg,
    info,
    paymentError,
  } = checkoutPage || {};

  const { header: infoHeader, bulletPoints: infoBulletPoints } = info || {};
  const error = queryError || upgradeError;
  const isPaymentError = paymentError || !(paymentIcon || paymentInfo);
  const loading = queryLoading || upgradeLoading;

  if (error) return <Error />;
  if (loading) return <FullScreenLoading loading spinnerWidth={4.4} />;

  return (
    <Layout
      header={{
        minimum: true,
        onCrossClick,
        onLogoClick,
      }}
    >
      {gbxMetaTitle && (
        <AcquisitionPageHead
          title={gbxMetaTitle}
          description={gbxMetaDescription}
        />
      )}
      <LearnMoreModal
        header={infoHeader}
        bulletPoints={infoBulletPoints}
        shouldSeelearnMoreModal
        {...{
          showModal,
          toggleModal,
          logAnalytics,
        }}
      />

      <Box
        margin="0 auto"
        textAlign="center"
        maxWidth="47.5rem"
        padding="0 1.4rem"
      >
        <ErrorBanner
          show={isPaymentError}
          errorMsg={checkoutnopaymenterrormsg}
        />
        {checkoutheader && (
          <Box margin="2rem 0 1rem 0" padding={['0 1.4rem', '0']}>
            <H1>{checkoutheader}</H1>
          </Box>
        )}

        <BorderBox padding="1.5rem">
          <H4>{checkoutpaymentsubheader}</H4>
          {checkoutupgradeprogram && (
            <Text paddingTop="1.5rem">{checkoutupgradeprogram}</Text>
          )}
          {checkoutprogramupgradeprice && (
            <Text
              paddingBottom="1rem"
              bold
              dangerouslySetInnerHTML={{ __html: checkoutprogramupgradeprice }}
            />
          )}
          {checkoutnewannualupgradeprice && (
            <Text
              paddingBottom="1rem"
              dangerouslySetInnerHTML={{
                __html: checkoutnewannualupgradeprice,
              }}
            />
          )}
          {isPaymentError ? (
            <Text>{NO_PAYMENT_METHOD_ERROR_MESSAGE}</Text>
          ) : (
            <PaymentMethod icon={paymentIcon} description={paymentInfo} />
          )}
          <Flex justifyContent="center" alignItems="center" marginTop="1rem">
            <Text small>{checkoutcancellationtext}</Text>
            <TextIconStyled
              as="span"
              className="icon-ipsy-question-circle"
              fontSize="1.6rem"
              onClick={toggleModal}
            />
          </Flex>
        </BorderBox>

        <BeigeBox margin={['2rem 0', '1.5rem 0']} padding="1.5rem">
          <Text small>{checkoutnotificationmonthlybulletpoint}</Text>
        </BeigeBox>

        {checkoutPage?.consent && (
          <Box pt={'2.4rem'} pb={'0.4rem'}>
            <Box textAlign={'left'}>
              {ReactHtmlParser(checkoutPage?.consent || '')}
            </Box>
          </Box>
        )}

        <Box margin={['1.5rem 0', '1.5rem 0']}>
          <BoldedButton
            disabled={isPaymentError}
            fullWidth
            data-e2e-selector="gbx-upgrade-checkout-button"
            onClick={handleUpgradeToGbx}
          >
            {checkoutbuttoncta}
          </BoldedButton>
        </Box>

        <Box textAlign={'left'}>
          <Disclaimers
            disclaimers={[
              checkoutsubscriptiondisclaimer,
              checkoutshippingdisclaimer?.concat(
                checkoutshippinglearnmore ?? ''
              ),
            ]}
          />
        </Box>
      </Box>
    </Layout>
  );
};

export default GbxUpgradeCheckout;
